import React from 'react';
import { Link } from 'react-router-dom';
import Sparkline from './Sparkline';
import BridgeSuggestion from './BridgeSuggestion';
import { whenLabel, bigNumber } from './MovingCard';
import { WhyBody, useWhy } from './WhyDrawer';

// One observation on the Insights page (2026-10 restyle). Same grammar as the
// Home card, laid out wide: the words on the left, the chart on the right.
// "Why →" opens the evidence and the question right under the card.

const Arrow = ({ dir, valence }) => {
  if (!dir) return null;
  const tone = valence === 'better' ? 'text-good-strong dark:text-good' : valence === 'worse' ? 'text-off-strong dark:text-off' : 'text-fg-3';
  return <span className={`${tone} text-[1.3rem] font-normal`} aria-hidden="true">{dir === 'up' ? '↑' : '↓'}</span>;
};

const Why = ({ item, onResolved, onClose, onAsk }) => {
  const { detail, failed } = useWhy(item.id, true);
  return (
    <div className="mt-2.5 border-t border-line pt-3.5 lg:col-span-2">
      <WhyBody id={item.id} detail={detail} failed={failed} onResolved={onResolved} onClose={onClose} onAsk={onAsk} />
    </div>
  );
};

const InsightCard = ({ item, open, onToggleWhy, onResolved, onAsk, compact = false }) => {
  if (!item) return null;
  const dot = item.valence === 'better' ? 'bg-good-strong dark:bg-good' : item.valence === 'worse' ? 'bg-off-strong dark:bg-off' : 'bg-fg-3';
  const big = bigNumber(item);
  const sentence = (item.headline || '').replace(/\.$/, '');

  return (
    <article className={`grid min-w-0 gap-x-7 gap-y-1.5 rounded-xl border bg-raised px-5 pb-4 pt-[18px] lg:grid-cols-[1fr_300px] ${open ? 'border-fg-3' : 'border-line'}`}>
      <div className="min-w-0">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-fg-3">
          <i className={`inline-block h-2 w-2 rounded-full ${dot}`} />
          {whenLabel(item)}
        </span>
        <div className="mt-1.5 font-medium text-fg-2">{item.chart?.label || ''}</div>
        {big && (
          <div className={`mt-1 flex items-baseline gap-2 font-medium tabular-nums tracking-[-0.03em] leading-none ${compact ? 'text-[1.6rem]' : 'text-[2rem]'}`}>
            <Arrow dir={big.dir} valence={item.valence} />
            {big.text}
            {big.small && <small className="text-base font-normal tracking-normal text-fg-3">{big.small}</small>}
          </div>
        )}
        <div className={`mt-2 font-medium leading-[1.3] tracking-[-0.01em] ${compact ? 'text-base' : 'text-[1.1rem]'}`}>{sentence}</div>
        {item.status === 'answered' && item.answer_choice && (
          <p className="mt-1.5 text-sm text-fg-3">You said: {item.answer_choice.replace(/_/g, ' ')}{item.answer_text ? `. ${item.answer_text}` : ''}</p>
        )}
        <div className="mt-2.5 flex flex-wrap items-center gap-4 text-sm">
          {item.kind === 'stack' ? (
            <Link to="/supplements" className="font-medium text-fg-2 hover:text-fg">See your stack →</Link>
          ) : null}
          {!compact && (
            <button type="button" onClick={() => onToggleWhy?.(item)} aria-expanded={open}
              className="bg-transparent p-0 font-medium text-fg-2 hover:text-fg">
              {open ? 'Hide why' : 'Why →'}
            </button>
          )}
        </div>
      </div>
      <div className="min-w-0 lg:self-center">
        <Sparkline chart={item.chart} valence={item.valence} height={compact ? 64 : 96} quiet />
      </div>
      {item.suggestion && !compact && <div className="lg:col-span-2"><BridgeSuggestion s={item.suggestion} /></div>}
      {open && <Why item={item} onResolved={onResolved} onClose={() => onToggleWhy?.(item)} onAsk={onAsk} />}
    </article>
  );
};

export default InsightCard;
